import { timepointLabel } from '../../api/know'
import { StudySchedulePanel } from '../../components/study/StudySchedulePanel'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Chart, registerables, type ChartConfiguration } from 'chart.js'
import { palette } from '@versports/theme'
import { club, type ClubPlayerInvitation, type ClubPlayerTimeline } from '../../api/club'
import { ApiError } from '../../api/client'
import { AboutButton } from '@versports/ui'
import { AppNavBar } from '../../components/AppNavBar'
import { ImpersonationBanner } from '../../components/ImpersonationBanner'
import { withAlpha } from '../../components/chartTheme'
import { onboardingBadge } from './ClubPage'

Chart.register(...registerables)

export function ClubPlayerResults() {
  const { userId } = useParams<{ userId: string }>()
  const [searchParams] = useSearchParams()
  const clubId = searchParams.get('clubId')
  const id = Number(userId)
  const cid = Number(clubId)

  const [data, setData] = useState<ClubPlayerTimeline | null>(null)
  const [invitations, setInvitations] = useState<ClubPlayerInvitation[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!Number.isFinite(id) || !Number.isFinite(cid)) return
    setError(null)
    try {
      setData(await club.playerTimeline(cid, id))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }, [id, cid])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!Number.isFinite(id) || !Number.isFinite(cid)) return
    club
      .playerInvitations(cid, id)
      .then((r) => setInvitations(r.items))
      .catch(() => {
        // Best-effort: la tabla de cuestionarios no bloquea el resto de la
        // vista si esto falla.
      })
  }, [id, cid])

  if (error) return <div className="banner-err">{error}</div>
  if (!data) return <p className="text-sm text-text-muted">Cargando…</p>

  const a = data.athlete
  return (
    <div className="flex min-h-screen flex-col">
      <ImpersonationBanner />

      <header className="border-b-2 border-primary bg-surface shadow-[0_12px_36px_rgba(226,90,27,0.12)]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-lg py-md">
          <div>
            <h1 className="m-0 text-xl font-bold text-text-primary">Resultados · {a.display_name || a.email}</h1>
          </div>
          <Link to="/club" className="btn-ghost px-md py-xs text-xs no-underline">
            ← Volver al panel
          </Link>
        </div>
      </header>

      <main className="flex-1 pb-24 px-lg py-lg">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm text-text-muted mb-lg">
            {a.email || '—'}
          </p>

          <OnboardingBreakdown onboarding={data.onboarding} />
          <StudySchedulePanel userId={id} clubId={cid} />

          <h2>Cuestionarios</h2>
          {invitations === null ? (
            <p className="text-sm text-text-muted">Cargando…</p>
          ) : invitations.length === 0 ? (
            <p className="mb-lg text-sm text-text-muted">Sin invitaciones a cuestionarios todavía.</p>
          ) : (
            <div className="table-wrap mb-lg">
              <table>
                <thead>
                  <tr>
                    <th>Cuestionario</th>
                    <th>Momento</th>
                    <th>Estado</th>
                    <th>Creada</th>
                    <th>Enviada</th>
                    <th>Respondida</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => (
                    <tr key={inv.id}>
                      <td>
                        <span className="tag">{inv.questionnaire_code}</span>
                      </td>
                      <td>
                        {inv.timepoint ? (
                          <span className="tag normal-case tracking-normal">{timepointLabel(inv.timepoint)}</span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <InvitationStatusBadge status={inv.status} />
                      </td>
                      <td className="text-sm text-text-secondary">
                        {new Date(inv.created_at).toLocaleDateString('es-AR')}
                      </td>
                      <td className="text-sm text-text-secondary">
                        {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString('es-AR') : '—'}
                      </td>
                      <td className="text-sm text-text-secondary">
                        {inv.answered_at ? new Date(inv.answered_at).toLocaleDateString('es-AR') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.events.length === 0 ? (
            <p className="text-sm text-text-muted">Sin cuestionarios ni sesiones todavía.</p>
          ) : (
            <>
              <TimelineChart events={data.events} />
              <h2>Eventos</h2>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Cuándo</th>
                      <th>Tipo</th>
                      <th>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.events.map((ev, i) => (
                      <tr key={i}>
                        <td className="text-sm">
                          {ev.date
                            ? new Date(ev.date).toLocaleString('es-AR', {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })
                            : '—'}
                        </td>
                        <td>
                          <span className={ev.type === 'session' ? 'tag-ok' : 'tag'}>
                            {ev.type === 'session' ? 'sesión' : 'cuestionario'}
                          </span>
                        </td>
                        <td className="text-xs text-text-muted">
                          {ev.type === 'questionnaire' ? (
                            <>
                              <strong className="text-text-primary">{ev.code}</strong> · {timepointLabel(ev.timepoint)}
                              {ev.scores.total && (
                                <>
                                  {' '}· Total:{' '}
                                  <strong className="text-text-primary">
                                    {ev.scores.total.raw}
                                  </strong>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {ev.n_trials} trials · goles {ev.goals} · atajadas {ev.saves}
                              {ev.goal_rate != null && (
                                <>
                                  {' '}· goal rate{' '}
                                  <strong className="text-text-primary">
                                    {Math.round(ev.goal_rate * 100)}%
                                  </strong>
                                </>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>

      <AboutButton navOffset />
      <AppNavBar />
    </div>
  )
}

function InvitationStatusBadge({ status }: { status: ClubPlayerInvitation['status'] }) {
  const map: Record<ClubPlayerInvitation['status'], { label: string; cls: string }> = {
    pending: { label: 'Pendiente', cls: 'tag' },
    scheduled: { label: 'Agendada', cls: 'tag' },
    sent: { label: 'Enviada', cls: 'tag-info' },
    answered: { label: 'Respondida', cls: 'tag-ok' },
    expired: { label: 'Vencida', cls: 'tag-err' },
  }
  const m = map[status]
  return <span className={`${m.cls} normal-case tracking-normal`}>{m.label}</span>
}

/**
 * Desglose del semáforo de onboarding (mismos flags/orden que la columna
 * "Onboarding" de la tab Plantel/Actividad — ver `onboardingBadge` en
 * `ClubPage.tsx`), acá con el detalle expandido ítem por ítem en vez de
 * un solo badge, porque esta es la vista de UN jugador puntual.
 */
function OnboardingBreakdown({ onboarding }: { onboarding: ClubPlayerTimeline['onboarding'] }) {
  const badge = onboardingBadge(onboarding)
  const items: Array<{ label: string; ok: boolean }> = [
    { label: 'Consentimiento', ok: onboarding.consent_ok },
    { label: 'Perfil', ok: onboarding.profile_ok },
    { label: 'Cuestionario', ok: onboarding.questionnaire_ok },
    { label: 'Casco (Train)', ok: onboarding.train_enabled },
  ]
  return (
    <div className="mb-lg flex flex-wrap items-center gap-md rounded border border-outline bg-surface px-md py-sm">
      <span className={badge.className} title={badge.title}>
        {badge.label}
      </span>
      <div className="flex flex-wrap gap-sm text-xs">
        {items.map((it) => (
          <span key={it.label} className={it.ok ? 'tag-ok' : 'tag'}>
            {it.ok ? '✓' : '✗'} {it.label}
          </span>
        ))}
      </div>
    </div>
  )
}

function TimelineChart({ events }: { events: ClubPlayerTimeline['events'] }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<Chart<'line'> | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    const dated = events.filter((e) => e.date != null)
    const labels = dated.map((e) =>
      new Date(e.date as string).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }),
    )
    const totalData = dated.map((e) =>
      e.type === 'questionnaire' && e.scores.total != null ? e.scores.total.raw : null,
    )
    const rateData = dated.map((e) =>
      e.type === 'session' && e.goal_rate != null ? Math.round(e.goal_rate * 100) : null,
    )

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Total cuestionario',
            data: totalData,
            yAxisID: 'yScore',
            borderColor: palette.primary,
            backgroundColor: withAlpha(palette.primary, 0.5),
            tension: 0.25,
            spanGaps: true,
          },
          {
            label: 'Goal rate (%)',
            data: rateData,
            yAxisID: 'yRate',
            borderColor: palette.textSecondary,
            backgroundColor: withAlpha(palette.textSecondary, 0.5),
            borderDash: [5, 4],
            tension: 0.25,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
          legend: { labels: { color: palette.textSecondary, boxWidth: 12 } },
        },
        scales: {
          x: {
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
          yScore: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            title: { display: true, text: 'Puntaje', color: palette.textMuted },
            ticks: { color: palette.textMuted },
            grid: { color: withAlpha(palette.outline, 0.4) },
          },
          yRate: {
            type: 'linear',
            position: 'right',
            beginAtZero: true,
            max: 100,
            title: { display: true, text: 'Goal rate %', color: palette.textMuted },
            ticks: { color: palette.textMuted },
            grid: { display: false },
          },
        },
      },
    }

    chartRef.current = new Chart(canvasRef.current, config)
    return () => {
      chartRef.current?.destroy()
      chartRef.current = null
    }
  }, [events])

  return (
    <div className="my-md" style={{ height: 300 }}>
      <canvas ref={canvasRef} />
    </div>
  )
}

